import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Building2,
  Calendar,
  CheckCircle2,
  DollarSign,
  Download,
  ExternalLink,
  FileCheck2,
  FileText,
  FilterX,
  Loader2,
  MapPin,
  Scale,
  Search,
  Trophy,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { Contract, Tender, TenderStatus } from '../types';
import { daysUntilCalendarDate, formatCalendarDate, parseCalendarDate } from '../utils/date';

const BRAZIL_STATES = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

const isWonStatus = (status: unknown) => status === TenderStatus.WON || status === 'WON' || status === 'Ganha';

const formatCurrency = (value?: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
};

const formatDate = (value?: string) => {
  return formatCalendarDate(value, '-');
};

const getYear = (value?: string) => {
  if (!value) return '';
  const parsed = parseCalendarDate(value);
  return parsed ? String(parsed.getUTCFullYear()) : '';
};

const getDaysSince = (value?: string) => {
  if (!value) return null;
  const remaining = daysUntilCalendarDate(value);
  return remaining === null ? null : Math.max(0, -remaining);
};

const buildDocumentUrl = (fileUrl?: string, tenderId?: string) => {
  if (!fileUrl) return null;
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  if (fileUrl.startsWith('uploads/') || fileUrl.includes('/uploads/')) {
    const key = fileUrl.replace(/^uploads\//, '');
    const query = tenderId ? `?tenderId=${encodeURIComponent(tenderId)}` : '';
    return `/api/uploads/${encodeURIComponent(key)}/view${query}`;
  }
  return `/api/s3/proxy?key=${encodeURIComponent(fileUrl)}`;
};

const csvCell = (value: unknown) => {
  const text = String(value ?? '').replace(/"/g, '""');
  return `"${text}"`;
};

const GanhasPage: React.FC = () => {
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('ALL');
  const [yearFilter, setYearFilter] = useState('ALL');
  const [contractFilter, setContractFilter] = useState<'ALL' | 'WITH' | 'WITHOUT'>('ALL');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [allTenders, allContracts] = await Promise.all([
          apiService.getTenders(),
          apiService.getContracts().catch(() => [] as Contract[]),
        ]);

        if (cancelled) return;
        setTenders(allTenders.filter((tender) => isWonStatus(tender.status)));
        setContracts(allContracts);
      } catch (err) {
        console.error('Erro ao carregar licitações homologadas:', err);
        if (!cancelled) setError('Não foi possível carregar os processos homologados.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const contractsByTender = useMemo(() => {
    return contracts.reduce<Record<string, Contract[]>>((acc, contract) => {
      if (!contract.tenderId) return acc;
      acc[contract.tenderId] = acc[contract.tenderId] || [];
      acc[contract.tenderId].push(contract);
      return acc;
    }, {});
  }, [contracts]);

  const availableYears = useMemo(() => {
    return Array.from(new Set(tenders.map((tender) => getYear(tender.deadline)).filter(Boolean))).sort((a, b) => Number(b) - Number(a));
  }, [tenders]);

  const filteredTenders = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return tenders.filter((tender) => {
      const linkedContracts = contractsByTender[tender.id] || [];
      const matchesSearch =
        !normalizedSearch ||
        tender.title.toLowerCase().includes(normalizedSearch) ||
        tender.organ.toLowerCase().includes(normalizedSearch) ||
        (tender.portal || '').toLowerCase().includes(normalizedSearch);

      const matchesState = stateFilter === 'ALL' || tender.state === stateFilter;
      const matchesYear = yearFilter === 'ALL' || getYear(tender.deadline) === yearFilter;
      const matchesContract =
        contractFilter === 'ALL' ||
        (contractFilter === 'WITH' ? linkedContracts.length > 0 : linkedContracts.length === 0);

      return matchesSearch && matchesState && matchesYear && matchesContract;
    });
  }, [contractFilter, contractsByTender, searchTerm, stateFilter, tenders, yearFilter]);

  const stats = useMemo(() => {
    const totalValue = tenders.reduce((sum, tender) => sum + Number(tender.value || 0), 0);
    const withContracts = tenders.filter((tender) => (contractsByTender[tender.id] || []).length > 0).length;
    const withDocuments = tenders.filter((tender) => Boolean(tender.fileUrl)).length;
    const ticket = tenders.length ? totalValue / tenders.length : 0;

    return { totalValue, withContracts, withDocuments, ticket };
  }, [contractsByTender, tenders]);

  const clearFilters = () => {
    setSearchTerm('');
    setStateFilter('ALL');
    setYearFilter('ALL');
    setContractFilter('ALL');
  };

  const exportCsv = () => {
    const headers = ['Objeto', 'Orgao', 'UF', 'Portal', 'Homologacao', 'Valor', 'Contratos vinculados', 'Documento'];
    const rows = filteredTenders.map((tender) => {
      const linkedContracts = contractsByTender[tender.id] || [];
      const documentUrl = buildDocumentUrl(tender.fileUrl, tender.id);
      return [
        tender.title,
        tender.organ,
        tender.state,
        tender.portal || '',
        formatDate(tender.deadline),
        Number(tender.value || 0).toFixed(2),
        linkedContracts.map((contract) => contract.contractNumber || contract.title).join(' | '),
        documentUrl || '',
      ];
    });

    const content = [headers, ...rows].map((row) => row.map(csvCell).join(';')).join('\n');
    const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `licitacoes-homologadas-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-4 sm:px-6 py-5 max-w-[1480px] mx-auto overflow-x-hidden">
      <div className="space-y-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] px-3 py-1 text-xs font-semibold text-brand-700 dark:text-brand-300">
              <Trophy className="w-3.5 h-3.5" />
              Processos homologados
            </div>
            <h2 className="mt-3 text-xl font-semibold text-ink-title dark:text-ink-title-dark">
              Licitações ganhas
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
              Centralize todas as licitações marcadas como ganhas/homologadas, acompanhe contratos resultantes e mantenha os documentos de comprovação organizados.
            </p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={filteredTenders.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-line dark:border-line-dark px-3 py-2 text-sm font-medium text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Homologadas</span>
              <Trophy className="w-4 h-4 text-brand-600" />
            </div>
            <div className="mt-2 text-2xl font-semibold text-ink-title dark:text-ink-title-dark">{tenders.length}</div>
          </div>
          <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Valor total</span>
              <DollarSign className="w-4 h-4 text-green-700 dark:text-green-300" />
            </div>
            <div className="mt-2 text-2xl font-semibold text-ink-title dark:text-ink-title-dark">{formatCurrency(stats.totalValue)}</div>
          </div>
          <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Com contrato</span>
              <Scale className="w-4 h-4 text-blue-700 dark:text-blue-300" />
            </div>
            <div className="mt-2 text-2xl font-semibold text-ink-title dark:text-ink-title-dark">{stats.withContracts}</div>
          </div>
          <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Ticket médio</span>
              <FileCheck2 className="w-4 h-4 text-purple-700 dark:text-purple-300" />
            </div>
            <div className="mt-2 text-2xl font-semibold text-ink-title dark:text-ink-title-dark">{formatCurrency(stats.ticket)}</div>
          </div>
        </div>

        <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
          <div className="border-b border-line dark:border-line-dark p-4">
            <div className="flex flex-col xl:flex-row gap-3">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar por objeto, órgão ou portal..."
                  className="input pl-10"
                />
              </div>
              <select value={stateFilter} onChange={(event) => setStateFilter(event.target.value)} className="input xl:w-36">
                <option value="ALL">UF: Todas</option>
                {BRAZIL_STATES.map((uf) => (
                  <option key={uf} value={uf}>{uf}</option>
                ))}
              </select>
              <select value={yearFilter} onChange={(event) => setYearFilter(event.target.value)} className="input xl:w-40">
                <option value="ALL">Ano: Todos</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <select value={contractFilter} onChange={(event) => setContractFilter(event.target.value as 'ALL' | 'WITH' | 'WITHOUT')} className="input xl:w-48">
                <option value="ALL">Contrato: Todos</option>
                <option value="WITH">Com contrato</option>
                <option value="WITHOUT">Sem contrato</option>
              </select>
              <button type="button" onClick={clearFilters} className="icon-btn xl:w-auto xl:px-3" title="Limpar filtros">
                <FilterX className="w-4 h-4" />
                <span className="hidden xl:inline text-sm">Limpar</span>
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center gap-3 py-16 text-ink-subtitle dark:text-ink-subtitle-dark">
              <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
              Carregando processos homologados...
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <AlertCircle className="w-10 h-10 text-red-500" />
              <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">{error}</p>
            </div>
          ) : filteredTenders.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <Trophy className="w-10 h-10 text-ink-placeholder dark:text-ink-placeholder-dark" />
              <p className="text-sm font-medium text-ink-title dark:text-ink-title-dark">Nenhum processo homologado encontrado</p>
              <p className="max-w-md text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                Marque uma licitação como “Ganha” na carteira para ela aparecer automaticamente neste menu.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-line dark:divide-line-dark">
              {filteredTenders.map((tender) => {
                const linkedContracts = contractsByTender[tender.id] || [];
                const daysSince = getDaysSince(tender.deadline);
                const documentUrl = buildDocumentUrl(tender.fileUrl, tender.id);

                return (
                  <article key={tender.id} className="p-4 hover:bg-surface-alt dark:hover:bg-surface-dark-alt transition-colors">
                    <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/40 px-2.5 py-1 text-xs font-semibold text-green-800 dark:text-green-200">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Homologado
                          </span>
                          {tender.state && (
                            <span className="inline-flex items-center gap-1 rounded border border-line dark:border-line-dark px-2 py-0.5 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                              <MapPin className="w-3 h-3" />
                              {tender.state}
                            </span>
                          )}
                          {linkedContracts.length > 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/40 px-2.5 py-1 text-xs font-semibold text-blue-800 dark:text-blue-200">
                              <Scale className="w-3.5 h-3.5" />
                              {linkedContracts.length} contrato(s)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 dark:bg-yellow-900/40 px-2.5 py-1 text-xs font-semibold text-yellow-800 dark:text-yellow-200">
                              <AlertCircle className="w-3.5 h-3.5" />
                              Sem contrato vinculado
                            </span>
                          )}
                        </div>

                        <h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark leading-snug">
                          {tender.title}
                        </h3>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" />
                            {tender.organ || 'Órgão não informado'}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Homologado em {formatDate(tender.deadline)}
                          </span>
                          {tender.portal && (
                            <span className="inline-flex items-center gap-1">
                              <FileText className="w-3.5 h-3.5" />
                              {tender.portal}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="xl:w-64 flex-shrink-0">
                        <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Valor homologado</div>
                          <div className="mt-1 text-lg font-semibold text-ink-title dark:text-ink-title-dark">{formatCurrency(tender.value)}</div>
                          <div className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                            {daysSince === null ? 'Data não informada' : `${daysSince} dia(s) desde homologação`}
                          </div>
                        </div>
                      </div>
                    </div>

                    {linkedContracts.length > 0 && (
                      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-2">
                        {linkedContracts.map((contract) => (
                          <div key={contract.id} className="rounded-md border border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt px-3 py-2 text-xs">
                            <div className="font-semibold text-ink-title dark:text-ink-title-dark">{contract.contractNumber || 'Contrato sem número'}</div>
                            <div className="text-ink-subtitle dark:text-ink-subtitle-dark truncate">{contract.title}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                      {documentUrl ? (
                        <a href={documentUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-md border border-line dark:border-line-dark px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt">
                          <ExternalLink className="w-4 h-4" />
                          Abrir edital/documento
                        </a>
                      ) : (
                        <button type="button" disabled className="inline-flex items-center gap-2 rounded-md border border-line dark:border-line-dark px-3 py-2 text-sm text-ink-subtitle dark:text-ink-subtitle-dark opacity-60">
                          <FileText className="w-4 h-4" />
                          Sem documento principal
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default GanhasPage;
